import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, type HelpdeskTicket } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { useGo, useParams } from '../../../navigation/nav';
import { Avatar, Badge, Button, Card, DataTable, DescList, ErrorState, Grid, Loading, Modal, PageHeader, Row, Screen, Select, Stack, StatusBadge, Text, TextField, useTheme } from '../../../ui';

const CATS: HelpdeskTicket['category'][] = ['payroll', 'leave', 'attendance', 'it', 'policy', 'other'];
const catLabel = (c: string) => (c === 'it' ? 'IT' : humanize(c));
const prioTone = (p: HelpdeskTicket['priority']) => (p === 'high' ? 'danger' : p === 'medium' ? 'warning' : 'neutral');

export function HelpdeskScreen() {
  const go = useGo();
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['tickets', 'mine', page], queryFn: () => api.ess.tickets.list({ scope: 'mine', page }) });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: 'payroll' as HelpdeskTicket['category'], subject: '', description: '', priority: 'medium' as HelpdeskTicket['priority'] });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const create = useMut(() => api.ess.tickets.create(f), {
    invalidate: [['tickets']],
    success: (t) => `Ticket ${t.number} created`,
    onSuccess: () => {
      setOpen(false);
      setF({ ...f, subject: '', description: '' });
      setErrs({});
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()}>
      <PageHeader title="Helpdesk" subtitle="Raise a query with HR, payroll or IT." actions={<Button variant="primary" icon="plus" title="New ticket" onPress={() => setOpen(true)} />} />
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={(t) => go('HelpdeskTicket', { id: t.id })}
        emptyTitle="No tickets"
        emptyDescription="Have a question? Raise a ticket and the right team will pick it up."
        columns={[
          { key: 'number', title: '#', width: 100 },
          { key: 'subject', title: 'Subject', width: 200, flex: 2 },
          { key: 'category', title: 'Category', width: 100, render: (t) => catLabel(t.category), sortValue: (t) => t.category },
          { key: 'priority', title: 'Priority', width: 90, render: (t) => <Badge tone={prioTone(t.priority)}>{humanize(t.priority)}</Badge>, sortValue: (t) => t.priority },
          { key: 'status', title: 'Status', width: 110, render: (t) => <StatusBadge status={t.status} />, sortValue: (t) => t.status },
          { key: 'created_at', title: 'Created', width: 110, render: (t) => fmtDate(t.created_at), sortValue: (t) => t.created_at },
        ]}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New ticket"
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button title="Create ticket" variant="primary" disabled={!f.subject.trim() || !f.description.trim()} loading={create.isPending} onPress={() => create.mutate(undefined)} />
          </>
        }
      >
        <Grid min={180} gap={12}>
          <Select label="Category" value={f.category} onChange={(category) => setF({ ...f, category })} error={errs.category} options={CATS.map((c) => ({ value: c, label: catLabel(c) }))} />
          <Select label="Priority" value={f.priority} onChange={(priority) => setF({ ...f, priority })} error={errs.priority} options={[{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }]} />
        </Grid>
        <TextField label="Subject" required value={f.subject} onChangeText={(subject) => setF({ ...f, subject })} error={errs.subject} />
        <TextField label="Description" required multiline value={f.description} onChangeText={(description) => setF({ ...f, description })} error={errs.description} style={{ minHeight: 120 }} />
      </Modal>
    </Screen>
  );
}

/** Ticket detail with conversation (hidden screen: helpdesk/:id). */
export function HelpdeskTicketScreen() {
  const { colors } = useTheme();
  const id = Number(useParams<{ id: string }>().id);
  const q = useQuery({ queryKey: ['tickets', 'one', id], queryFn: () => api.ess.tickets.get(id), enabled: !!id });
  const [body, setBody] = useState('');
  const comment = useMut((b: string) => api.ess.tickets.comment(id, b), { invalidate: [['tickets']], onSuccess: () => setBody('') });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <Screen><PageHeader title="Ticket" /><ErrorState error={q.error} onRetry={() => q.refetch()} /></Screen>;
  const t = q.data;
  return (
    <Screen maxWidth={860} refreshing={q.isRefetching} onRefresh={() => q.refetch()}>
      <PageHeader title={t.subject} subtitle={t.number} />
      <Row>
        <StatusBadge status={t.status} />
        <Badge tone={prioTone(t.priority)}>{`${humanize(t.priority)} priority`}</Badge>
        <Badge tone="info">{catLabel(t.category)}</Badge>
      </Row>
      <Card>
        <DescList
          items={[
            ['Raised by', t.employee?.full_name],
            ['Created', `${fmtDate(t.created_at)} ${fmtTime(t.created_at)}`],
            ['Assignee', t.assignee?.name ?? 'Unassigned'],
          ]}
        />
        <View style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: colors.surface2 }}>
          <Text>{t.description}</Text>
        </View>
      </Card>
      <Card title="Conversation">
        <Stack gap={14}>
          {(t.comments ?? []).length === 0 && <Text variant="muted">No replies yet.</Text>}
          {(t.comments ?? []).map((c) => (
            <Row key={c.id} align="flex-start" wrap={false}>
              <Avatar name={c.user.name} size={28} />
              <View style={{ flex: 1 }}>
                <Text weight="semibold">
                  {c.user.name} <Text variant="small">{fmtDate(c.created_at)} {fmtTime(c.created_at)}</Text>
                </Text>
                <Text>{c.body}</Text>
              </View>
            </Row>
          ))}
          {t.status !== 'closed' && (
            <Stack gap={8}>
              <TextField multiline placeholder="Write a reply…" accessibilityLabel="Reply" value={body} onChangeText={setBody} />
              <View style={{ alignItems: 'flex-end' }}>
                <Button title="Reply" icon="send" variant="primary" disabled={!body.trim()} loading={comment.isPending} onPress={() => comment.mutate(body)} />
              </View>
            </Stack>
          )}
        </Stack>
      </Card>
    </Screen>
  );
}
